import type { ReportDetail, ReportListItem } from '../types/report';
import type { ErrorEnvelope } from '../types/envelope';
import {
  ApiError,
  NetworkError,
  SessionExpiredError,
} from './ai-api.service';

export { SessionExpiredError, NetworkError, ApiError };

export interface ReportsApiServiceOptions {
  apiUrl: string;
  sessionToken: string;
}

export class ReportsApiService {
  constructor(private readonly opts: ReportsApiServiceOptions) {}

  private get headers(): HeadersInit {
    return { Authorization: `Bearer ${this.opts.sessionToken}` };
  }

  private base(): string {
    return this.opts.apiUrl.replace(/\/$/, '');
  }

  async list(): Promise<ReportListItem[]> {
    const data = await this.getJson<{ data: ReportListItem[] }>(
      `${this.base()}/ai-module/reports`,
    );
    return data.data;
  }

  async get(id: number): Promise<ReportDetail> {
    const data = await this.getJson<{ data: ReportDetail }>(
      `${this.base()}/ai-module/reports/${id}`,
    );
    return data.data;
  }

  private async getJson<T>(url: string): Promise<T> {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: this.headers,
      });
    } catch (err) {
      throw new NetworkError(err);
    }
    if (response.ok) {
      return (await response.json()) as T;
    }
    const body = (await response.json().catch(() => null)) as ErrorEnvelope | null;
    if (response.status === 401) {
      throw new SessionExpiredError(
        body?.error_code === 'SESSION_INVALID' ? 'invalid' : 'expired',
      );
    }
    throw new ApiError(
      body?.content ?? `HTTP ${response.status}`,
      body?.error_code ?? 'INTERNAL',
    );
  }
}
